import type {
  CancelForSupplyFailureInput,
  CompleteBankRefundInput,
  ConfirmDepositInput,
  MarkOrderDeliveredInput,
  NotifyOrderDelayInput,
  OrderBadge,
  OrderDetail,
  RefundOrderCancelInput,
  RejectOrderCancelInput,
  RemoveOrderShipmentInput,
  ShipOrderInput,
  StartProductionInput,
  UpdateOrderAddressInput,
} from '@repo/api-contracts';
export type OrderRefundResult =
  | { kind: 'COMPLETED'; order: OrderDetail }
  | { kind: 'UNKNOWN'; message: string }
  | { kind: 'MANUAL_PENDING' };
export interface OrderActions {
  confirmDeposit: (input: ConfirmDepositInput) => Promise<void>;
  completeBankRefund: (input: CompleteBankRefundInput) => Promise<void>;
  startProduction: (input: StartProductionInput) => Promise<unknown>;
  notifyDelay: (input: NotifyOrderDelayInput) => Promise<unknown>;
  ship: (input: ShipOrderInput) => Promise<unknown>;
  addShipments: (input: ShipOrderInput) => Promise<unknown>;
  removeShipment: (input: RemoveOrderShipmentInput) => Promise<unknown>;
  retryTracking: (input: RemoveOrderShipmentInput) => Promise<unknown>;
  markDelivered: (input: MarkOrderDeliveredInput) => Promise<unknown>;
  updateShippingAddress: (input: UpdateOrderAddressInput) => Promise<unknown>;
  refundCancel: (input: RefundOrderCancelInput) => Promise<OrderRefundResult>;
  rejectCancelRequest: (input: RejectOrderCancelInput) => Promise<unknown>;
  cancelForSupplyFailure: (input: CancelForSupplyFailureInput) => Promise<OrderRefundResult>;
}
export const orderBadgeLabels: Record<OrderBadge, string> = {
  CANCEL_REQUESTED: '취소 요청',
  DELAYED: '지연',
  ADDRESS_UPDATED: '배송지 변경됨',
  WITHDRAWN_USER: '탈퇴 회원',
  TRACKING_EXPIRED: '추적 종료',
  RETURNED: '반송',
  TRACKING_REGISTER_FAILED: '추적 등록 실패',
  DESTINATION_NOT_SHIPPED: '송장 없는 배송지',
};
export function orderErrorCode(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  if (
    'data' in error &&
    typeof error.data === 'object' &&
    error.data !== null &&
    'orderErrorCode' in error.data &&
    typeof error.data.orderErrorCode === 'string'
  )
    return error.data.orderErrorCode;
  return undefined;
}
export function orderErrorMessage(error: unknown): string {
  const code = orderErrorCode(error);
  if (code === 'REFUND_IN_PROGRESS') return '결제 상세에서 처리 중인 환불을 확인하세요';
  if (code === 'NOTHING_TO_REFUND') return '환불할 승인 결제가 없습니다.';
  if (code === 'ORDER_STATE_CONFLICT')
    return '주문 상태가 변경되었습니다. 최신 상태를 확인한 뒤 다시 처리해 주세요.';
  if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof error.message === 'string'
  )
    return error.message;
  return '처리하지 못했습니다. 다시 시도해 주세요.';
}
export function koreanDate(value: string | Date): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(value));
}
export function refundResultMessage(result: OrderRefundResult): string {
  return result.kind === 'MANUAL_PENDING'
    ? '환불 대기로 바뀌었습니다. 송금 후 [환불 완료]를 눌러 주세요.'
    : result.kind === 'UNKNOWN'
      ? '환불 확인 중'
      : result.order.status === 'REFUNDED'
        ? '환불 완료'
        : '환불 결과 반영 확인 중';
}
